import { useState, useRef } from 'react'
import logo from '../../assets/minister.png'
import backgroundImage from '../../assets/background.jfif'
import { verifyOtp } from '../../services/auth.services'

interface OtpPageProps {
  userId: string
  onVerifySuccess: () => void
  onCancel?: () => void
}

export function OtpPage({ userId, onVerifySuccess, onCancel }: OtpPageProps) {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''))
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Handle digit input & auto-focus next input
  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return // Numeric only

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1) // Take last character if typed over
    setOtp(newOtp)
    setError('')

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  // Handle backspace navigation
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  // Handle multi-digit paste
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').trim()
    if (!/^\d+$/.test(pastedData)) return

    const digits = pastedData.slice(0, 6).split('')
    const newOtp = [...otp]
    digits.forEach((digit, index) => {
      newOtp[index] = digit
    })
    setOtp(newOtp)

    const focusIndex = Math.min(digits.length, 5)
    inputRefs.current[focusIndex]?.focus()
  }

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault()
  const otpCode = otp.join('')

  if (otpCode.length < 6) {
    setError('၆ လုံးပါသော ကုတ်နံပါတ် အပြည့်အစုံ ထည့်သွင်းပါ။')
    return
  }

  setIsLoading(true)
  setError('')

  try {
    const response = await verifyOtp({
      userId,
      code: otpCode, // 👈 code: otpCode ဟု ပို့ပေးရမည်
    })

    // Access Token နဲ့ User info သိမ်းဆည်းခြင်း
    localStorage.setItem('access_token', response.access_token)
    localStorage.setItem('user', JSON.stringify(response.user))

    onVerifySuccess()
  } catch (err: unknown) {
    if (err instanceof Error) {
      setError(err.message)
    } else {
      setError('ကုတ်နံပါတ် စစ်ဆေးရာတွင် အဆင်မပြေပါ၊ ပြန်လည်ကြိုးစားပါ။')
    }
  } finally {
    setIsLoading(false)
  }
}

  return (
    <div
      style={{ backgroundImage: `url(${backgroundImage})` }}
      className="relative flex min-h-screen flex-col items-center justify-center bg-cover bg-center bg-no-repeat p-6 font-sans"
    >
      <div className="absolute inset-0 bg-slate-950/50 pointer-events-none" />

      {/* Card Wrapper */}
      <div className="z-10 w-full max-w-md space-y-6 rounded-2xl border border-white/10 bg-white/95 p-8 shadow-2xl backdrop-blur-sm transition-all duration-300">
        
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden bg-transparent">
            <img src={logo} alt="တံဆိပ်တုံး" className="h-full w-full object-contain" />
          </div>
          <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
            ၂ ဆင့် အတည်ပြုခြင်း (2FA / OTP)
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            သင့် Authenticator အက်ပ်မှ ၆ လုံးပါ ကုတ်နံပါတ်ကို ထည့်သွင်းပါ။
          </p>
        </div>

        {/* OTP Form */}
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isLoading}
                className="h-12 w-12 rounded-lg border border-slate-300 text-center text-xl font-bold text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            ))}
          </div>

          {error && <p className="text-center text-sm font-medium text-red-600">{error}</p>}

          <div className="space-y-2">
            <button
              type="submit"
              disabled={isLoading || otp.join('').length < 6}
              className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-blue-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  စစ်ဆေးနေပါသည်...
                </span>
              ) : (
                'အတည်ပြုမည်'
              )}
            </button>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="w-full text-center text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors py-1"
              >
                နောက်သို့ ပြန်သွားမည်
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}